import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Box,
  Typography,
  TextField,
  Card,
  CardContent,
  Grid,
  Stack,
  CircularProgress,
  InputAdornment,
  Button,
  Chip,
  Fade,
  Divider,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar,
} from "@mui/material";
import {
  CloudDone,
  Language,
  Security,
  Lock,
  Upgrade,
  Save,
  Launch,
  Image as ImageIcon,
  CheckCircle,
  FiberManualRecord,
} from "@mui/icons-material";
import axios from "axios";
import { jwtDecode } from "jwt-decode";
import config from "../config";
import { toast } from "react-toastify";

// ── Brand tokens ──────────────────────────────────────────────────────────
const T = {
  dark: "#2E4053",
  darkDeep: "#1C2A3D",
  muted: "#34495E",
  border: "#A6AAAD",
  surface: "#F4F6F9",
  surfaceAlt: "#D1D8E0",
  white: "#FFFFFF",
  success: "#16A34A",
  successBg: "#F0FDF4",
  pending: "#D97706",
  pendingBg: "#FFFBEB",
  danger: "#DC2626",
  dangerBg: "#FEF2F2",
};

const DISPLAY_FONT = "'Manrope', 'Inter', -apple-system, sans-serif";
const BODY_FONT = "'Inter', -apple-system, sans-serif";

// Injects the Manrope display face once, if it isn't already on the page.
const useDisplayFont = () => {
  useEffect(() => {
    if (document.getElementById("spade-manrope-font")) return;
    const link = document.createElement("link");
    link.id = "spade-manrope-font";
    link.rel = "stylesheet";
    link.href =
      "https://fonts.googleapis.com/css2?family=Manrope:wght@600;700;800&display=swap";
    document.head.appendChild(link);
  }, []);
};

const PLAN_RANK = { free: 0, premium: 1, enterprise: 2, admin: 2 };

// Normalizes whatever shape the backend's ssl_status column happens to hold
// into one of four states we know how to render.
const normalizeDomainStatus = ({ hasDomain, sslStatus, dnsVerified }) => {
  if (!hasDomain) return "none";
  const s = (sslStatus || "").toLowerCase();
  if (s === "active" && dnsVerified) return "live";
  if (s === "error" || s === "failed") return "error";
  return "pending";
};

const STATUS_META = {
  none: {
    label: "No domain yet",
    color: T.border,
    bg: T.surface,
    fg: T.muted,
    pulse: false,
  },
  pending: {
    label: "Provisioning SSL",
    color: T.pending,
    bg: T.pendingBg,
    fg: T.pending,
    pulse: true,
  },
  live: {
    label: "Live & secured",
    color: T.success,
    bg: T.successBg,
    fg: T.success,
    pulse: false,
  },
  error: {
    label: "Verification failed",
    color: T.danger,
    bg: T.dangerBg,
    fg: T.danger,
    pulse: false,
  },
};

// The signature element: a compact, real status readout of the async SSL /
// DNS provisioning your backend already runs — previously invisible in the UI.
const DomainStatusPill = ({ status }) => {
  const meta = STATUS_META[status];
  return (
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      sx={{
        bgcolor: meta.bg,
        border: `1px solid ${meta.color}33`,
        borderRadius: 10,
        px: 1.5,
        py: 0.6,
        width: "fit-content",
      }}
    >
      <Box sx={{ position: "relative", width: 10, height: 10 }}>
        {meta.pulse && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              borderRadius: "50%",
              bgcolor: meta.color,
              opacity: 0.5,
              animation: "spade-pulse 1.6s ease-out infinite",
              "@keyframes spade-pulse": {
                "0%": { transform: "scale(1)", opacity: 0.6 },
                "100%": { transform: "scale(2.6)", opacity: 0 },
              },
            }}
          />
        )}
        <FiberManualRecord sx={{ fontSize: 10, color: meta.color }} />
      </Box>
      <Typography
        variant="caption"
        sx={{ fontWeight: 700, color: meta.fg, letterSpacing: 0.2 }}
      >
        {meta.label}
      </Typography>
    </Stack>
  );
};

// Numbered rail marker for the setup flow — order is meaningful here: the
// backend genuinely provisions subdomain → identity → SSL in that sequence.
const StepMarker = ({ n, done }) => (
  <Box
    sx={{
      width: 28,
      height: 28,
      borderRadius: "50%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      bgcolor: done ? T.dark : "transparent",
      border: `1.5px solid ${done ? T.dark : T.border}`,
      color: done ? T.white : T.muted,
      fontFamily: DISPLAY_FONT,
      fontWeight: 800,
      fontSize: 13,
      flexShrink: 0,
    }}
  >
    {done ? <CheckCircle sx={{ fontSize: 18 }} /> : n}
  </Box>
);

const ShopSetting = () => {
  useDisplayFont();

  const [token] = useState(localStorage.getItem("token"));
  const [userPlan, setUserPlan] = useState("free");
  const [saveStatus, setSaveStatus] = useState("idle");
  const [hasChanges, setHasChanges] = useState(false);
  const [openConfirm, setOpenConfirm] = useState(false);
  const [loading, setLoading] = useState(true);

  const [originalSubdomain, setOriginalSubdomain] = useState("");
  const [shopData, setShopData] = useState({
    subdomain: "",
    customDomain: "",
    site_title: "",
    favicon: null,
    sslStatus: null,
    dnsVerified: false,
  });

  const [faviconFile, setFaviconFile] = useState(null);
  const [faviconUploading, setFaviconUploading] = useState(false);
  const fileInputRef = useRef(null);

  const isSubdomainLocked = useMemo(
    () => !!originalSubdomain,
    [originalSubdomain]
  );

  const { plan, isFree, isPremiumPlus, isEnterprise } = useMemo(() => {
    const p = (userPlan || "free").toLowerCase();
    const rank = PLAN_RANK[p] ?? 0;
    return {
      plan: p,
      isFree: rank < 1,
      isPremiumPlus: rank >= 1,
      isEnterprise: rank >= 2,
    };
  }, [userPlan]);

  const domainStatus = useMemo(
    () =>
      normalizeDomainStatus({
        hasDomain: Boolean(shopData.subdomain || shopData.customDomain),
        sslStatus: shopData.sslStatus,
        dnsVerified: shopData.dnsVerified,
      }),
    [
      shopData.subdomain,
      shopData.customDomain,
      shopData.sslStatus,
      shopData.dnsVerified,
    ]
  );

  useEffect(() => {
    if (token) {
      try {
        const decoded = jwtDecode(token);
        setUserPlan((decoded.subscription_type || "free").toLowerCase());
      } catch (err) {
        console.error(err);
      }
      fetchSettings();
    }
  }, [token]);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${config.baseURL}/api/shop/settings`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const s = res.data?.data || res.data || {};
      let subdomainOnly = s.subdomain || "";
      if (subdomainOnly.endsWith(".spadeplatform.com")) {
        subdomainOnly = subdomainOnly.replace(".spadeplatform.com", "");
      }

      setShopData({
        subdomain: subdomainOnly,
        customDomain: s.customDomain || "",
        site_title: s.site_title || "",
        favicon: s.favicon || null,
        sslStatus: s.sslStatus || null,
        dnsVerified: Boolean(s.dnsVerified),
      });

      setOriginalSubdomain(subdomainOnly);
    } catch (err) {
      toast.error("Failed to load settings");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setShopData((prev) => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const handleSaveClick = () => {
    if (!originalSubdomain && shopData.subdomain) setOpenConfirm(true);
    else processSave();
  };

  const processSave = async () => {
    setOpenConfirm(false);
    setSaveStatus("saving");

    try {
      const payload = {
        subdomain: shopData.subdomain || null,
        customDomain: shopData.customDomain || null,
        site_title: shopData.site_title || null,
      };

      const res = await axios.put(
        `${config.baseURL}/api/shop/settings`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (res?.data?.success) {
        toast.success("Settings saved");
      }

      await fetchSettings();
      setHasChanges(false);
      setSaveStatus("saved");
      setTimeout(() => setSaveStatus("idle"), 2500);
    } catch (err) {
      setSaveStatus("error");
      toast.error(err.response?.data?.message ?? "Save failed");
    }
  };

  const handleFaviconPick = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFaviconFile(file);
    setShopData((prev) => ({ ...prev, favicon: URL.createObjectURL(file) }));
  };

  const handleFaviconUpload = async () => {
    if (!faviconFile) return;
    setFaviconUploading(true);
    try {
      const form = new FormData();
      form.append("favicon", faviconFile);
      const res = await axios.post(
        `${config.baseURL}/api/shop/settings/favicon`,
        form,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );
      if (res?.data?.success) {
        toast.success("Favicon uploaded");
        setShopData((prev) => ({ ...prev, favicon: res.data.favicon }));
        setFaviconFile(null);
      }
    } catch (err) {
      toast.error(err.response?.data?.message ?? "Favicon upload failed");
    } finally {
      setFaviconUploading(false);
    }
  };

  const getFullUrl = () => {
    if (isEnterprise && shopData.customDomain)
      return `${shopData.customDomain}`;
    return shopData.subdomain
      ? `${shopData.subdomain}.spadeplatform.com`
      : "your-shop.spadeplatform.com";
  };

  const LockedOverlay = ({ planType }) => (
    <Box
      sx={{
        position: "absolute",
        inset: -8,
        zIndex: 10,
        backgroundColor: "rgba(244, 246, 249, 0.88)",
        backdropFilter: "blur(3px)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        borderRadius: 2,
        border: `1.5px dashed ${T.border}`,
        m: 1,
      }}
    >
      <Chip
        icon={<Lock sx={{ fontSize: "0.9rem !important" }} />}
        label={`${planType} feature`}
        size="small"
        sx={{
          mb: 1,
          fontWeight: 700,
          bgcolor: T.surfaceAlt,
          color: T.dark,
        }}
      />
      <Button
        variant="contained"
        size="small"
        startIcon={<Upgrade sx={{ fontSize: 16 }} />}
        sx={{
          bgcolor: T.dark,
          textTransform: "none",
          borderRadius: 5,
          fontFamily: BODY_FONT,
          "&:hover": { bgcolor: T.darkDeep },
        }}
        onClick={() => (window.location.href = "/billing")}
      >
        Upgrade plan
      </Button>
    </Box>
  );

  const SectionHeading = ({ step, done, icon, title, subtitle }) => (
    <Stack direction="row" spacing={2} alignItems="flex-start" sx={{ mb: 3 }}>
      <StepMarker n={step} done={done} />
      <Box>
        <Stack direction="row" spacing={1} alignItems="center">
          {icon}
          <Typography
            sx={{
              fontFamily: DISPLAY_FONT,
              fontWeight: 800,
              fontSize: 17,
              color: T.dark,
              letterSpacing: -0.2,
            }}
          >
            {title}
          </Typography>
        </Stack>
        {subtitle && (
          <Typography
            variant="body2"
            sx={{ color: T.muted, mt: 0.25, fontFamily: BODY_FONT }}
          >
            {subtitle}
          </Typography>
        )}
      </Box>
    </Stack>
  );

  if (loading)
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "80vh",
        }}
      >
        <CircularProgress sx={{ color: T.dark }} />
      </Box>
    );

  return (
    <Box
      sx={{
        backgroundColor: T.surface,
        minHeight: "100vh",
        fontFamily: BODY_FONT,
      }}
    >
      {/* STICKY HEADER */}
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 10,
          backgroundColor: "rgba(255,255,255,0.92)",
          backdropFilter: "blur(10px)",
          borderBottom: `1px solid ${T.surfaceAlt}`,
          px: { xs: 2, md: 6 },
          py: 2.25,
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          flexWrap="wrap"
          rowGap={1}
        >
          <Box>
            <Typography
              sx={{
                fontFamily: DISPLAY_FONT,
                fontWeight: 800,
                fontSize: 22,
                color: T.dark,
                letterSpacing: -0.5,
              }}
            >
              Store settings
            </Typography>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ mt: 0.75 }}
            >
              <Chip
                label={plan.toUpperCase()}
                size="small"
                variant="outlined"
                sx={{
                  fontWeight: 700,
                  fontSize: "0.65rem",
                  height: 20,
                  borderColor: T.dark,
                  color: T.dark,
                }}
              />
              {hasChanges && (
                <Chip
                  label="Unsaved changes"
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: "0.65rem",
                    bgcolor: T.surfaceAlt,
                    color: T.darkDeep,
                    fontWeight: 700,
                  }}
                />
              )}
            </Stack>
          </Box>
          <Stack direction="row" spacing={2} alignItems="center">
            <Fade in={saveStatus !== "idle"}>
              <Chip
                icon={
                  saveStatus === "saving" ? (
                    <CircularProgress size={14} sx={{ color: "inherit" }} />
                  ) : (
                    <CloudDone sx={{ fontSize: 18 }} />
                  )
                }
                label={saveStatus === "saving" ? "Saving…" : "Saved"}
                sx={{
                  bgcolor: saveStatus === "error" ? T.dangerBg : T.successBg,
                  color: saveStatus === "error" ? T.danger : T.success,
                  fontWeight: 700,
                }}
              />
            </Fade>
            <Button
              variant="contained"
              disableElevation
              startIcon={<Save sx={{ fontSize: 18 }} />}
              disabled={!hasChanges || saveStatus === "saving" || isFree}
              onClick={handleSaveClick}
              sx={{
                bgcolor: T.dark,
                borderRadius: 2,
                px: 3,
                py: 1,
                textTransform: "none",
                fontWeight: 700,
                fontFamily: BODY_FONT,
                boxShadow: "0 2px 8px rgba(46,64,83,0.25)",
                "&:hover": { bgcolor: T.darkDeep },
                "&.Mui-disabled": { bgcolor: T.surfaceAlt, color: "#94a3b8" },
              }}
            >
              Save settings
            </Button>
          </Stack>
        </Stack>
      </Box>

      <Box sx={{ p: { xs: 2, md: 6 }, maxWidth: "1200px", margin: "0 auto" }}>
        {isFree && (
          <Alert
            severity="warning"
            icon={<Upgrade />}
            action={
              <Button
                size="small"
                variant="contained"
                sx={{
                  bgcolor: T.dark,
                  textTransform: "none",
                  fontWeight: 700,
                  "&:hover": { bgcolor: T.darkDeep },
                }}
                onClick={() => (window.location.href = "/billing")}
              >
                Upgrade
              </Button>
            }
            sx={{ mb: 3, borderRadius: 2, alignItems: "center" }}
          >
            You're on the Free plan — subdomain, site title, custom domain, and
            favicon all require Premium or Enterprise.
          </Alert>
        )}

        <Grid container spacing={4}>
          {/* FORM AREA — a 3-step setup rail */}
          <Grid item xs={12} md={7}>
            <Stack spacing={3}>
              <Card
                sx={{
                  borderRadius: 3,
                  boxShadow: "0 1px 2px rgba(46,64,83,0.06)",
                  border: `1px solid ${T.surfaceAlt}`,
                  overflow: "visible",
                }}
              >
                <CardContent sx={{ p: 4 }}>
                  <SectionHeading
                    step={1}
                    done={isSubdomainLocked}
                    icon={<Language sx={{ color: T.dark, fontSize: 20 }} />}
                    title="Domain"
                    subtitle="Where shoppers find your store."
                  />

                  <Box sx={{ pl: { xs: 0, sm: 5.5 } }}>
                    <Typography
                      variant="body2"
                      sx={{ mb: 1, fontWeight: 600, color: T.muted }}
                    >
                      Shop subdomain
                    </Typography>
                    <Box sx={{ position: "relative", mb: 4 }}>
                      {!isPremiumPlus && <LockedOverlay planType="Premium" />}
                      <TextField
                        fullWidth
                        disabled={isSubdomainLocked || !isPremiumPlus}
                        value={shopData.subdomain}
                        onChange={(e) =>
                          handleChange(
                            "subdomain",
                            e.target.value
                              .toLowerCase()
                              .replace(/[^a-z0-9-]/g, "")
                          )
                        }
                        placeholder="shop-name"
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              .spadeplatform.com
                            </InputAdornment>
                          ),
                          startAdornment: isSubdomainLocked && (
                            <InputAdornment position="start">
                              <Lock sx={{ fontSize: 16, color: T.muted }} />
                            </InputAdornment>
                          ),
                        }}
                        helperText={
                          isSubdomainLocked
                            ? "Locked after initial setup — this can't be changed."
                            : "Lowercase letters, numbers, and hyphens only."
                        }
                        sx={{
                          "& .MuiOutlinedInput-root.Mui-focused fieldset": {
                            borderColor: T.dark,
                          },
                        }}
                      />
                    </Box>

                    <Divider sx={{ mb: 4, borderColor: T.surfaceAlt }} />

                    <Typography
                      variant="body2"
                      sx={{ mb: 1, fontWeight: 600, color: T.muted }}
                    >
                      Custom domain
                    </Typography>
                    <Box sx={{ position: "relative" }}>
                      {!isEnterprise && <LockedOverlay planType="Enterprise" />}
                      <TextField
                        fullWidth
                        disabled={!isEnterprise}
                        placeholder="www.yourstore.com"
                        value={shopData.customDomain}
                        onChange={(e) =>
                          handleChange("customDomain", e.target.value)
                        }
                        sx={{
                          "& .MuiOutlinedInput-root.Mui-focused fieldset": {
                            borderColor: T.dark,
                          },
                        }}
                      />
                    </Box>

                    <Stack direction="row" sx={{ mt: 2.5 }}>
                      <DomainStatusPill status={domainStatus} />
                    </Stack>
                  </Box>
                </CardContent>
              </Card>

              <Card
                sx={{
                  borderRadius: 3,
                  boxShadow: "0 1px 2px rgba(46,64,83,0.06)",
                  border: `1px solid ${T.surfaceAlt}`,
                  overflow: "visible",
                }}
              >
                <CardContent sx={{ p: 4 }}>
                  <SectionHeading
                    step={2}
                    done={isPremiumPlus && Boolean(shopData.site_title)}
                    icon={<Security sx={{ color: T.dark, fontSize: 20 }} />}
                    title="Identity"
                    subtitle="How your store appears in search and browser tabs."
                  />

                  <Box sx={{ pl: { xs: 0, sm: 5.5 } }}>
                    <Box
                      sx={{ position: "relative", mb: isEnterprise ? 4 : 0 }}
                    >
                      {!isPremiumPlus && <LockedOverlay planType="Premium" />}
                      <TextField
                        fullWidth
                        disabled={!isPremiumPlus}
                        label="Site title"
                        value={shopData.site_title}
                        onChange={(e) =>
                          handleChange("site_title", e.target.value)
                        }
                        helperText="Shown on search engines and browser tabs."
                        sx={{
                          "& .MuiOutlinedInput-root.Mui-focused fieldset": {
                            borderColor: T.dark,
                          },
                        }}
                      />
                    </Box>

                    {isEnterprise && (
                      <>
                        <Divider sx={{ mb: 4, borderColor: T.surfaceAlt }} />
                        <Typography
                          variant="body2"
                          sx={{ mb: 1.5, fontWeight: 600, color: T.muted }}
                        >
                          Favicon
                        </Typography>
                        <Stack direction="row" spacing={2} alignItems="center">
                          <Avatar
                            variant="rounded"
                            src={shopData.favicon || undefined}
                            sx={{
                              width: 44,
                              height: 44,
                              bgcolor: T.surface,
                              border: `1px solid ${T.surfaceAlt}`,
                            }}
                          >
                            <ImageIcon sx={{ color: T.border, fontSize: 20 }} />
                          </Avatar>
                          <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*"
                            hidden
                            onChange={handleFaviconPick}
                          />
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => fileInputRef.current?.click()}
                            sx={{
                              borderColor: T.border,
                              color: T.dark,
                              textTransform: "none",
                              fontWeight: 600,
                              borderRadius: 2,
                            }}
                          >
                            Choose image
                          </Button>
                          <Button
                            variant="contained"
                            size="small"
                            disabled={!faviconFile || faviconUploading}
                            onClick={handleFaviconUpload}
                            sx={{
                              bgcolor: T.dark,
                              textTransform: "none",
                              fontWeight: 700,
                              borderRadius: 2,
                              minWidth: 84,
                              "&:hover": { bgcolor: T.darkDeep },
                              "&.Mui-disabled": {
                                bgcolor: T.surfaceAlt,
                                color: "#94a3b8",
                              },
                            }}
                          >
                            {faviconUploading ? (
                              <CircularProgress
                                size={16}
                                sx={{ color: T.white }}
                              />
                            ) : (
                              "Upload"
                            )}
                          </Button>
                        </Stack>
                      </>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Stack>
          </Grid>

          {/* PREVIEW SIDEBAR — a minimal browser-chrome mockup */}
          <Grid item xs={12} md={5}>
            <Box sx={{ position: "sticky", top: 110 }}>
              <SectionHeading
                step={3}
                done={false}
                icon={null}
                title="Go live"
                subtitle="This is what shoppers will see and visit."
              />

              <Card
                sx={{
                  borderRadius: 3,
                  overflow: "hidden",
                  border: `1px solid ${T.surfaceAlt}`,
                  boxShadow: "0 8px 24px rgba(46,64,83,0.08)",
                }}
              >
                {/* Browser chrome bar */}
                <Box
                  sx={{
                    bgcolor: "#EDEFF2",
                    px: 1.5,
                    py: 1,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    borderBottom: `1px solid ${T.surfaceAlt}`,
                  }}
                >
                  <Stack direction="row" spacing={0.6} sx={{ pl: 0.5 }}>
                    {["#ED6A5E", "#F4BF4F", "#61C454"].map((c) => (
                      <Box
                        key={c}
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          bgcolor: c,
                        }}
                      />
                    ))}
                  </Stack>
                  <Box
                    sx={{
                      flex: 1,
                      bgcolor: T.white,
                      borderRadius: 5,
                      px: 1.5,
                      py: 0.5,
                      display: "flex",
                      alignItems: "center",
                      gap: 0.75,
                      border: `1px solid ${T.surfaceAlt}`,
                    }}
                  >
                    <Lock sx={{ fontSize: 12, color: T.muted }} />
                    <Typography
                      variant="caption"
                      noWrap
                      sx={{
                        color: T.muted,
                        fontFamily: "monospace",
                        fontSize: 12,
                      }}
                    >
                      {getFullUrl()}
                    </Typography>
                  </Box>
                </Box>

                {/* Page body preview */}
                <Box
                  sx={{
                    bgcolor: T.dark,
                    p: 4,
                    textAlign: "center",
                    color: T.white,
                  }}
                >
                  <Avatar
                    variant="rounded"
                    src={shopData.favicon || undefined}
                    sx={{
                      width: 40,
                      height: 40,
                      mx: "auto",
                      mb: 2,
                      bgcolor: "rgba(255,255,255,0.1)",
                    }}
                  >
                    {(shopData.site_title || "S")[0].toUpperCase()}
                  </Avatar>
                  <Typography
                    sx={{
                      fontFamily: DISPLAY_FONT,
                      fontWeight: 800,
                      fontSize: 18,
                      mb: 0.5,
                    }}
                  >
                    {shopData.site_title || "Your Shop Title"}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ opacity: 0.6, mb: 3, fontFamily: BODY_FONT }}
                  >
                    Storefront preview
                  </Typography>
                  <Button
                    fullWidth
                    variant="contained"
                    href={`https://${getFullUrl()}`}
                    target="_blank"
                    disabled={!shopData.subdomain}
                    sx={{
                      bgcolor: T.white,
                      color: T.dark,
                      py: 1.25,
                      fontWeight: 700,
                      textTransform: "none",
                      borderRadius: 2,
                      "&:hover": { bgcolor: T.surfaceAlt },
                      "&.Mui-disabled": {
                        bgcolor: "rgba(255,255,255,0.15)",
                        color: "rgba(255,255,255,0.5)",
                      },
                    }}
                  >
                    Launch shop <Launch sx={{ ml: 1, fontSize: 16 }} />
                  </Button>
                </Box>
              </Card>

              <Alert
                severity="info"
                sx={{
                  mt: 3,
                  borderRadius: 2,
                  bgcolor: "transparent",
                  border: `1px solid ${T.surfaceAlt}`,
                  color: T.muted,
                }}
              >
                A clear site title helps your shop rank higher in search
                results.
              </Alert>
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* CONFIRMATION DIALOG */}
      <Dialog
        open={openConfirm}
        onClose={() => setOpenConfirm(false)}
        PaperProps={{ sx: { borderRadius: 3, p: 1 } }}
      >
        <DialogTitle
          sx={{ fontFamily: DISPLAY_FONT, fontWeight: 800, color: T.dark }}
        >
          Confirm subdomain
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            You're about to set your shop link to:
          </Typography>
          <Box
            sx={{
              bgcolor: T.surface,
              p: 2,
              borderRadius: 2,
              textAlign: "center",
              mb: 2,
            }}
          >
            <Typography
              sx={{ fontFamily: DISPLAY_FONT, fontWeight: 800, color: T.dark }}
            >
              {shopData.subdomain}.spadeplatform.com
            </Typography>
          </Box>
          <Alert severity="warning" sx={{ fontWeight: 600 }}>
            Once saved, the subdomain can't be changed.
          </Alert>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setOpenConfirm(false)} color="inherit">
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={processSave}
            sx={{
              bgcolor: T.dark,
              textTransform: "none",
              fontWeight: 700,
              "&:hover": { bgcolor: T.darkDeep },
            }}
          >
            Confirm & save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ShopSetting;
